import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useMediaQuery from '@hooks/useMediaQuery';

/**
 * HomePage — Production version matching live MRT
 *
 * Sections (from live site, top to bottom):
 * 1. Hero carousel — rotating promotional banners (data-driven)
 * 2. Information & news — welcome text
 * 3. Guided Tool Finder — text + image + CTA (left/right layout)
 * 4. Benefits — text + image + CTA
 * 5. Download Center — text + image + catalog PDF download buttons
 * 6. Rock Tools services — 4-card grid with images
 *
 * All content driven by homeContent config (future: CMS-editable)
 * All images use placeholder paths — swappable by admin asset management
 */

// ============================================================================
// DATA — Hero carousel slides (future: loaded from CMS/JSON)
// ============================================================================

const HERO_SLIDES = [
  {
    id: 1,
    headline: 'Top Hammer drilling tools',
    subheadline: 'Precision engineered for underground and surface drilling',
    image: '/images/hero/hero-tophammer.jpg',
    cta: { label: 'Explore products', to: '/products/top-hammer' },
    bgColor: '#A08C64', // gold side
  },
  {
    id: 2,
    headline: 'Down-the-hole drilling tools',
    subheadline: 'When you need a bigger bang',
    image: '/images/hero/hero-dth.jpg',
    cta: { label: 'Explore products', to: '/products/dth' },
    bgColor: '#A08C64',
  },
  {
    id: 3,
    headline: 'Rotary drilling tools',
    subheadline: 'Built for the toughest surface mining conditions',
    image: '/images/hero/hero-rotary.jpg',
    cta: { label: 'Explore products', to: '/products/rotary' },
    bgColor: '#A08C64',
  },
  {
    id: 4,
    headline: 'Raiseboring tools',
    subheadline: 'Complete solutions for raise drilling',
    image: '/images/hero/hero-raiseboring.jpg',
    cta: { label: 'Explore products', to: '/products/raiseboring' },
    bgColor: '#A08C64',
  },
];

// ============================================================================
// DATA — Content sections (future: CMS-editable)
// ============================================================================

const CONTENT_SECTIONS = [
  {
    id: 'tool-finder',
    eyebrow: 'Guided Tool Finder',
    headline: 'Research, shop and compare',
    body: 'Unsure which rock tools are suited for your needs? Try out the Tool Finder function with guided questions specifically designed to help you here.',
    cta: { label: 'Get started', to: '/tool-finder' },
    image: '/images/home/guided-tool-finder.jpg',
    imageAlt: 'Sandvik engineers discussing rock tools',
  },
  {
    id: 'benefits',
    eyebrow: 'Benefits',
    headline: 'Sandvik products speak for themselves',
    body: 'Discover what makes Sandvik products stand out and what benefits and values Sandvik rock tools and services can bring your operation.',
    cta: { label: 'Get started', to: '/products' },
    image: '/images/home/benefits.jpg',
    imageAlt: 'Close-up of Sandvik rock drilling tools',
  },
  {
    id: 'download',
    eyebrow: 'Manual and technical specs',
    headline: 'Visit our Download Center',
    body: 'Here you can find technical documentation for Sandvik Mining and Rock Solutions products, as well as safety data sheets (SDS) and safety information sheets (SIS).',
    cta: { label: 'Get started', to: '/documents' },
    image: '/images/home/download-center.jpg',
    imageAlt: 'DTH hammers RH560 documentation',
    catalogButtons: [
      { label: 'Download Top Hammer catalog(PDF)', href: '/documents/th-catalog.pdf' },
      { label: 'Download Down The Hole catalog(pdf)', href: '/documents/dth-catalog.pdf' },
    ],
  },
];

const SERVICES = [
  {
    id: 'inventory',
    title: 'Inventory management',
    image: '/images/home/service-inventory.jpg',
    to: '/contact?topic=inventory',
  },
  {
    id: 'recycling',
    title: 'Carbide recycling',
    image: '/images/home/service-recycling.jpg',
    to: '/contact?topic=recycling',
  },
  {
    id: 'support',
    title: 'Product support',
    image: '/images/home/service-support.jpg',
    to: '/contact?topic=support',
  },
  {
    id: 'contract',
    title: 'Contract services',
    image: '/images/home/service-contract.jpg',
    to: '/contact?topic=contract',
  },
];

// ============================================================================
// HERO CAROUSEL COMPONENT
// ============================================================================

function HeroCarousel({ slides }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { isMobile } = useMediaQuery();

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isPaused, slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const slide = slides[currentSlide];
  if (!slide) return null;

  return (
    <section className="relative overflow-hidden">
      {/* Slide content */}
      <div className="relative h-[400px] md:h-[480px] lg:h-[520px]">
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
          {/* Left — text on gold/brand bg */}
          <div
            className="flex flex-col justify-center px-6 md:px-12 lg:px-16 py-8"
            style={{ backgroundColor: slide.bgColor || '#A08C64' }}
          >
            <h1 className="font-sandvik-headline font-normal text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-3">
              {slide.headline}
            </h1>
            {slide.subheadline && (
              <p className="text-white/90 text-lg font-sandvik-text mb-6 max-w-md">
                {slide.subheadline}
              </p>
            )}
            {slide.cta && (
              <Link
                to={slide.cta.to}
                className="inline-flex items-center gap-2 bg-electric-blue hover:bg-eb-3 text-white text-sm font-normal px-6 py-3 rounded-none transition-colors font-sandvik-text w-fit"
              >
                {slide.cta.label}
                <span className="material-symbols-outlined text-sm">chevron_right</span>
              </Link>
            )}
          </div>

          {/* Right — product image on dark bg */}
          <div className="hidden md:block bg-[#101010] relative">
            <img
              src={slide.image}
              alt={slide.headline}
              className="absolute inset-0 w-full h-full object-cover object-center"
              onError={(e) => {
                // Fallback: show dark bg with placeholder text
                e.target.style.display = 'none';
              }}
            />
            {/* Fallback placeholder when image doesn't exist */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-n5">
                <span className="material-symbols-outlined text-5xl mb-2 block">image</span>
                <p className="text-xs font-sandvik-text">Hero image placeholder</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Carousel controls — dots + pause */}
      <div className="flex items-center justify-center gap-2 py-4 bg-white">
        {/* Pause/play button */}
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="p-1 text-carbon-black hover:text-electric-blue transition-colors"
          aria-label={isPaused ? 'Play carousel' : 'Pause carousel'}
        >
          <span className="material-symbols-outlined text-lg">
            {isPaused ? 'play_arrow' : 'pause'}
          </span>
        </button>

        {/* Dot indicators — square style per mockup #21 */}
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors
              ${index === currentSlide
                ? 'bg-carbon-black'
                : 'bg-n3 hover:bg-n5'}`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentSlide ? 'true' : undefined}
          />
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// CONTENT SECTION COMPONENT (text+image pattern from live site)
// ============================================================================

function ContentSection({ section, index }) {
  const isEven = index % 2 === 0;

  return (
    <section className="border-t border-n2">
      <div className="sandvik-container py-12 md:py-16">
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center ${isEven ? '' : 'md:grid-flow-dense'}`}>
          {/* Text side */}
          <div className={isEven ? '' : 'md:col-start-1'}>
            {section.eyebrow && (
              <p className="text-sm text-n5 font-sandvik-text mb-2">
                {section.eyebrow}
              </p>
            )}
            <h2 className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-4">
              {section.headline}
            </h2>
            <p className="text-sm text-n6 font-sandvik-text leading-relaxed mb-6 max-w-lg">
              {section.body}
            </p>
            {section.cta && (
              <Link
                to={section.cta.to}
                className="inline-flex items-center gap-1 text-sm text-carbon-black underline hover:text-electric-blue transition-colors font-sandvik-text"
              >
                {section.cta.label}
                <span className="material-symbols-outlined text-sm text-vibrant-orange">chevron_right</span>
              </Link>
            )}

            {/* Catalog download buttons (Download Center section) */}
            {section.catalogButtons && (
              <div className="mt-6">
                <p className="text-sm text-carbon-black font-sandvik-text font-normal mb-3">
                  Download our Rock Tools catalogs below
                </p>
                <div className="flex flex-wrap gap-3">
                  {section.catalogButtons.map((btn) => (
                    <a
                      key={btn.label}
                      href={btn.href}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-n2 bg-white hover:bg-n1 text-sm text-carbon-black font-sandvik-text transition-colors rounded-none"
                    >
                      <span className="material-symbols-outlined text-lg text-n5">description</span>
                      {btn.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Image side */}
          <div className={isEven ? 'md:col-start-2' : 'md:col-start-2'}>
            <div className="relative aspect-[4/3] bg-n1 overflow-hidden">
              <img
                src={section.image}
                alt={section.imageAlt || section.headline}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {/* Placeholder when image missing */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-n4">
                  <span className="material-symbols-outlined text-4xl mb-1 block">image</span>
                  <p className="text-xs font-sandvik-text">{section.eyebrow || 'Image'} placeholder</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SERVICES GRID COMPONENT
// ============================================================================

function ServicesGrid({ services }) {
  return (
    <section className="bg-n1">
      <div className="sandvik-container py-12 md:py-16">
        <h2 className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-8">
          Rock Tools services
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {services.map((service) => (
            <Link
              key={service.id}
              to={service.to}
              className="group"
            >
              <div className="relative aspect-[4/3] bg-n2 overflow-hidden mb-3">
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {/* Placeholder */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="material-symbols-outlined text-3xl text-n4">image</span>
                </div>
              </div>
              <p className="text-sm font-sandvik-text text-carbon-black group-hover:text-electric-blue transition-colors">
                {service.title}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// MAIN HOMEPAGE
// ============================================================================

export default function HomePage() {
  return (
    <div>
      {/* 1. Hero carousel */}
      <HeroCarousel slides={HERO_SLIDES} />

      {/* 2. Information & news */}
      <section className="sandvik-container py-10 md:py-14">
        <h2 className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-4">
          Information & news
        </h2>
        <p className="text-sm text-n6 font-sandvik-text leading-relaxed max-w-4xl">
          Welcome to the new online channel for Sandvik rock tools. It is currently available in Canada, Mexico, South Africa and USA. We are adding more locations constantly. Until then, we request customers to continue using My Sandvik portal for purchases. If you are located in the countries listed above, please sign in to continue your shopping experience. New users are most welcome to try our online web shop as a registered user. If you have questions or comments, please use the Contact us option from the top menu.
        </p>
      </section>

      {/* 3-5. Content sections (Tool Finder, Benefits, Download Center) */}
      {CONTENT_SECTIONS.map((section, index) => (
        <ContentSection key={section.id} section={section} index={index} />
      ))}

      {/* 6. Rock Tools services */}
      <ServicesGrid services={SERVICES} />
    </div>
  );
}
